import { useCallback, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { DateType } from '../../Analytics/types';
import { calculateDatesFromType } from '../../utils/dates';

export interface AdsQueryVariables {
  clientId?: string;
  startDate: string;
  endDate: string;
  dateType: DateType;
  showNonAttributedData: boolean;
  fromOldData: boolean;
  adAccountIds?: string[];
  adCampaignAndSetIds?: string[];
  adObjectiveIds?: string[];
  memberIds?: string[];
  compareWithPartnershipAds?: boolean;
}

interface AdsUrlVariables {
  clientId?: string;
  startDate?: string;
  endDate?: string;
  dateType: DateType;
  showNonAttributedData: boolean;
  fromOldData: boolean;
  adAccountIds?: string[];
  adCampaignAndSetIds?: string[];
  adObjectiveIds?: string[];
  memberIds?: string[];
  compareWithPartnershipAds?: boolean;
}

interface UseAdsAnalyticsParamsProps {
  clientId?: string;
  defaultVariables: Omit<AdsQueryVariables, 'clientId'>;
  clientCreatedDate?: string;
}

/**
 * Helper function to calculate and validate start/end dates based on URL variables and client created date
 */
const calculateValidatedDates = (
  urlVariables: AdsUrlVariables,
  clientCreatedDate?: string,
): { startDate: string; endDate: string } => {
  // If custom date type with valid dates provided, use them directly
  if (urlVariables.dateType === 'custom' && urlVariables.startDate && urlVariables.endDate) {
    return {
      startDate: urlVariables.startDate,
      endDate: urlVariables.endDate,
    };
  }

  // Otherwise, calculate dates from the date type
  const clientCreatedDateObj = clientCreatedDate
    ? (() => {
        const d = new Date(clientCreatedDate);
        d.setUTCHours(0, 0, 0, 0);
        return d;
      })()
    : undefined;

  const calculatedDates = calculateDatesFromType(urlVariables.dateType, clientCreatedDateObj);

  // Normalize dates to string format
  return {
    startDate:
      calculatedDates.startDate instanceof Date
        ? calculatedDates.startDate.toISOString().split('T')[0]
        : calculatedDates.startDate,
    endDate:
      calculatedDates.endDate instanceof Date
        ? calculatedDates.endDate.toISOString().split('T')[0]
        : calculatedDates.endDate,
  };
};

export const useAdsAnalyticsParams = ({
  clientId,
  defaultVariables,
  clientCreatedDate,
}: UseAdsAnalyticsParamsProps) => {
  const location = useLocation();
  const history = useHistory();

  const updateUrlParams = useCallback(
    (variables: AdsQueryVariables) => {
      // Preserve existing URL parameters by initializing with current search params
      const params = new URLSearchParams(location.search);
      params.set('dateType', variables.dateType);
      if (variables.dateType === 'custom') {
        if (variables.startDate) params.set('startDate', variables.startDate);
        if (variables.endDate) params.set('endDate', variables.endDate);
      } else {
        // Remove custom date params when not using custom date type
        params.delete('startDate');
        params.delete('endDate');
      }
      if (variables.showNonAttributedData !== undefined) {
        params.set('showNonAttributedData', variables.showNonAttributedData.toString());
      }
      if (variables.fromOldData !== undefined) {
        params.set('fromOldData', variables.fromOldData.toString());
      }

      // Handle adAccountIds
      if (variables.adAccountIds && variables.adAccountIds.length > 0) {
        params.set('adAccountIds', variables.adAccountIds.join(','));
      } else {
        params.delete('adAccountIds');
      }

      // Handle adCampaignAndSetIds
      if (variables.adCampaignAndSetIds && variables.adCampaignAndSetIds.length > 0) {
        params.set('adCampaignAndSetIds', variables.adCampaignAndSetIds.join(','));
      } else {
        params.delete('adCampaignAndSetIds');
      }

      // Handle adObjectiveIds
      if (variables.adObjectiveIds && variables.adObjectiveIds.length > 0) {
        params.set('adObjectiveIds', variables.adObjectiveIds.join(','));
      } else {
        params.delete('adObjectiveIds');
      }

      // Handle memberIds
      if (variables.memberIds && variables.memberIds.length > 0) {
        params.set('memberIds', variables.memberIds.join(','));
      } else {
        params.delete('memberIds');
      }

      // Handle compareWithPartnershipAds
      if (variables.compareWithPartnershipAds !== undefined) {
        params.set('compareWithPartnershipAds', variables.compareWithPartnershipAds.toString());
      } else {
        params.delete('compareWithPartnershipAds');
      }

      history.replace({ pathname: location.pathname, search: params.toString() });
    },
    [history, location.pathname, location.search],
  );

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const currentParams = Object.fromEntries(searchParams.entries());
  const dateType = (currentParams.dateType as DateType) || defaultVariables.dateType;

  // Parse adAccountIds from URL
  const adAccountIds = useMemo(() => {
    const adAccountIdsParam = currentParams.adAccountIds;
    return adAccountIdsParam ? adAccountIdsParam.split(',').filter(Boolean) : defaultVariables.adAccountIds || [];
  }, [currentParams.adAccountIds, defaultVariables.adAccountIds]);

  // Parse adCampaignAndSetIds from URL
  const adCampaignAndSetIds = useMemo(() => {
    const adCampaignAndSetIdsParam = currentParams.adCampaignAndSetIds;
    return adCampaignAndSetIdsParam
      ? adCampaignAndSetIdsParam.split(',').filter(Boolean)
      : defaultVariables.adCampaignAndSetIds || [];
  }, [currentParams.adCampaignAndSetIds, defaultVariables.adCampaignAndSetIds]);

  // Parse adObjectiveIds from URL
  const adObjectiveIds = useMemo(() => {
    const adObjectiveIdsParam = currentParams.adObjectiveIds;
    return adObjectiveIdsParam ? adObjectiveIdsParam.split(',').filter(Boolean) : defaultVariables.adObjectiveIds || [];
  }, [currentParams.adObjectiveIds, defaultVariables.adObjectiveIds]);

  // Parse memberIds from URL
  const memberIds = useMemo(() => {
    const memberIdsParam = currentParams.memberIds;
    return memberIdsParam ? memberIdsParam.split(',').filter(Boolean) : defaultVariables.memberIds || [];
  }, [currentParams.memberIds, defaultVariables.memberIds]);

  // Parse compareWithPartnershipAds from URL
  const compareWithPartnershipAds = useMemo(() => {
    return currentParams.compareWithPartnershipAds === 'true' || defaultVariables.compareWithPartnershipAds || false;
  }, [currentParams.compareWithPartnershipAds, defaultVariables.compareWithPartnershipAds]);

  // Parse fromOldData from URL
  const fromOldData = useMemo(() => {
    return currentParams.fromOldData === 'true' || defaultVariables.fromOldData;
  }, [currentParams.fromOldData, defaultVariables.fromOldData]);

  const urlVariables = useMemo<AdsUrlVariables>(
    () => ({
      clientId,
      dateType,
      ...(dateType === 'custom'
        ? {
            startDate: currentParams.startDate,
            endDate: currentParams.endDate,
          }
        : {}),
      showNonAttributedData: currentParams.showNonAttributedData === 'true' || defaultVariables.showNonAttributedData,
      fromOldData,
      adAccountIds,
      adCampaignAndSetIds,
      adObjectiveIds,
      memberIds,
      compareWithPartnershipAds,
    }),
    [
      clientId,
      dateType,
      currentParams,
      defaultVariables.showNonAttributedData,
      fromOldData,
      adAccountIds,
      adCampaignAndSetIds,
      adObjectiveIds,
      memberIds,
      compareWithPartnershipAds,
    ],
  );

  // Convert URL variables to query variables by ensuring dates are set
  const queryVariables = useMemo<AdsQueryVariables>(() => {
    const { startDate, endDate } = calculateValidatedDates(urlVariables, clientCreatedDate);

    return {
      clientId,
      dateType: urlVariables.dateType,
      startDate,
      endDate,
      showNonAttributedData: urlVariables.showNonAttributedData,
      fromOldData: urlVariables.fromOldData,
      adAccountIds: urlVariables.adAccountIds,
      adCampaignAndSetIds: urlVariables.adCampaignAndSetIds,
      adObjectiveIds: urlVariables.adObjectiveIds,
      memberIds: urlVariables.memberIds,
      compareWithPartnershipAds: urlVariables.compareWithPartnershipAds,
    };
  }, [urlVariables, clientCreatedDate, clientId]);

  const handleDateRangeSubmit = useCallback(
    (values: { dateType: DateType; dateRange?: { from?: string; to?: string } }) => {
      const variables: AdsQueryVariables = {
        clientId,
        dateType: values.dateType,
        startDate: queryVariables.startDate,
        endDate: queryVariables.endDate,
        showNonAttributedData: urlVariables.showNonAttributedData,
        fromOldData: urlVariables.fromOldData,
        adAccountIds: queryVariables.adAccountIds,
        adCampaignAndSetIds: queryVariables.adCampaignAndSetIds,
        adObjectiveIds: queryVariables.adObjectiveIds,
        memberIds: queryVariables.memberIds,
        compareWithPartnershipAds: queryVariables.compareWithPartnershipAds,
      };

      if (values.dateType === 'custom' && values.dateRange?.from && values.dateRange?.to) {
        // Validate that start date is before or equal to end date
        const startDate = new Date(values.dateRange.from);
        const endDate = new Date(values.dateRange.to);

        // Check for invalid dates
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
          console.warn('Invalid date range provided:', values.dateRange);
          return; // Exit early without updating URL
        }

        // Check that start date is not after end date
        if (startDate > endDate) {
          console.warn('Start date cannot be after end date:', {
            startDate: values.dateRange.from,
            endDate: values.dateRange.to,
          });
          return; // Exit early without updating URL
        }

        variables.startDate = values.dateRange.from;
        variables.endDate = values.dateRange.to;
      }

      updateUrlParams(variables);
    },
    [
      clientId,
      urlVariables.showNonAttributedData,
      urlVariables.fromOldData,
      queryVariables.startDate,
      queryVariables.endDate,
      queryVariables.adAccountIds,
      queryVariables.adCampaignAndSetIds,
      queryVariables.adObjectiveIds,
      queryVariables.memberIds,
      queryVariables.compareWithPartnershipAds,
      updateUrlParams,
    ],
  );

  const handleShowNonAttributedDataChange = useCallback(
    (showNonAttributedData: boolean) => {
      updateUrlParams({
        ...queryVariables,
        showNonAttributedData,
      });
    },
    [updateUrlParams, queryVariables],
  );

  const handleFromOldDataChange = useCallback(
    (fromOldData: boolean) => {
      updateUrlParams({
        ...queryVariables,
        fromOldData,
      });
    },
    [updateUrlParams, queryVariables],
  );

  // Add filter handling function
  const handleApplyFilters = useCallback(
    (filterData: {
      adAccountIds?: string[];
      adCampaignAndSetIds?: string[];
      adObjectiveIds?: string[];
      memberIds?: string[];
      compareWithPartnershipAds?: boolean;
      showNonAttributedData?: boolean;
    }) => {
      updateUrlParams({
        ...queryVariables,
        adAccountIds: filterData.adAccountIds,
        adCampaignAndSetIds: filterData.adCampaignAndSetIds,
        adObjectiveIds: filterData.adObjectiveIds,
        memberIds: filterData.memberIds,
        compareWithPartnershipAds: filterData.compareWithPartnershipAds,
        showNonAttributedData: filterData.showNonAttributedData,
      });
    },
    [updateUrlParams, queryVariables],
  );

  return {
    queryVariables,
    updateUrlParams,
    handleDateRangeSubmit,
    handleShowNonAttributedDataChange,
    handleFromOldDataChange,
    handleApplyFilters,
  };
};
